import { MapPinpoint } from '@application/assets';
import { NetworkIcon } from '@application/components';
import { rem } from '@application/styles/utils';
import { formatUtcDateOnly } from '@application/utils/date-utils';
import { NetworkCode, Site, Amenity } from '@domain/graphql.types';
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import MapPin from '../components/MapPin';
import SiteLookupContext from '../context/SiteLookupContext';
import SiteLookupMapContext from '../context/SiteLookupMapContext';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { RealTimeLocationChips, RealTimeLocationUtilization, RealTimeLocationStationsTable } from '../components/RealTimeLocationDataComponents';
import HistoricalViewChips, { HistoricalUtilizationHistogram, ToHistogramBuckets } from '../components/HistoricalViewContainer';

/** Get minutes since midnight for "now" in a specific IANA timezone. */
function minutesSinceMidnightInTZ(now: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const hh = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const mm = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return hh * 60 + mm;
}

const weekdayKey = (): keyof Amenity['openingHours'] => {
  const keys: Array<keyof Amenity['openingHours']> = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return keys[new Date().getDay()];
};

// Average walking speed is approximately 80 meters per minute.
const AVERAGE_WALKING_SPEED_M_PER_MIN = 80;
const toMinutes = (m: number) => {
  return Math.ceil(m / AVERAGE_WALKING_SPEED_M_PER_MIN);
};

const formatHours = (hours: Amenity['openingHours'], timeZone = 'UTC'): string => {
  const key = weekdayKey();
  const ranges = hours?.[key] ?? [];
  if (!ranges.length) return 'Closed';

  const now = new Date();
  const nowMinutes = minutesSinceMidnightInTZ(now, timeZone);
  let openingTime = null;
  for (const r of ranges) {
    const [sh, sm] = r.open.split(':').map(Number);
    const [eh, em] = r.close.split(':').map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;

    if (nowMinutes < start) {
      // hasn't opened yet
      if (!openingTime || start < openingTime) {
        openingTime = start;
      }
    }
    if (nowMinutes >= start && nowMinutes < end) {
      // open now
      return `Closes at ${toAmPm(end)}`;
    }
  }
  if (openingTime !== null) {
    return `Opens at ${toAmPm(openingTime)}`;
  }
  return 'Closed';
};

const toAmPm = (minutes: number) => {
  let h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ampm = h % 24 >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
};

type Opts = {
  isExpanded: boolean;
  topOffset?: number; // account for sticky header etc.
  scrollContainerId?: string; // optional explicit container
  watchTransition?: boolean; // if your expand animates via CSS transition
};

/** Find the nearest scrollable ancestor. Returns an HTMLElement (never Document). */
function getScrollableAncestor(el: HTMLElement | null, explicitId?: string): HTMLElement {
  // 1) Explicit container by id
  if (explicitId) {
    const node = document.getElementById(explicitId);
    if (node) return node as HTMLElement;
  }

  // 2) Nearest scrollable ancestor
  let node: HTMLElement | null = el;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const oy = style.overflowY;
    const canScroll = (oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight;
    if (canScroll) return node;
    node = node.parentElement;
  }

  // 3) Fallback to the page scroll root (HTMLElement)
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

export function useScrollIntoViewOnExpand(
  ref: React.RefObject<HTMLElement | null>,
  { isExpanded, topOffset = 0, scrollContainerId, watchTransition = true }: Opts,
) {
  const roRef = useRef<ResizeObserver | null>(null);
  const pendingRef = useRef<number | null>(null);

  const doScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const container = getScrollableAncestor(el, scrollContainerId);
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

    const pageRoot = (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
    const isPage = container === pageRoot;

    const elRect = el.getBoundingClientRect();

    if (isPage) {
      const target = window.scrollY + elRect.top - topOffset - 160;
      window.scrollTo({
        top: Math.max(0, target),
        behavior: prefersReduced ? 'auto' : 'smooth',
      });
    } else {
      const cRect = container.getBoundingClientRect();
      const target = container.scrollTop + (elRect.top - cRect.top) - topOffset;
      container.scrollTo({
        top: Math.max(0, target),
        behavior: prefersReduced ? 'auto' : 'smooth',
      });
    }
  }, [scrollContainerId, topOffset, ref]);

  // Use layout effect so we measure after React commits but before paint
  useLayoutEffect(() => {
    if (!isExpanded || !ref.current) return;
    if (watchTransition) {
      let done = false;

      const onEnd = (e: Event) => {
        if (e instanceof TransitionEvent && e.propertyName !== 'height') return;
        if (done) return;
        done = true;
        doScroll();
      };

      ref.current.addEventListener('transitionend', onEnd, { once: true });

      // Fallback: if there is no transition, still scroll next frame.
      pendingRef.current = window.requestAnimationFrame(() => {
        if (!done) doScroll();
      });

      return () => {
        ref.current?.removeEventListener('transitionend', onEnd);
        if (pendingRef.current) {
          cancelAnimationFrame(pendingRef.current);
          pendingRef.current = null;
        }
      };
    }

    // If no transition, wait for size to settle using ResizeObserver (covers async content)
    if (!roRef.current) {
      roRef.current = new ResizeObserver(() => {
        // Debounce to the next frame
        if (pendingRef.current) cancelAnimationFrame(pendingRef.current);
        pendingRef.current = window.requestAnimationFrame(doScroll);
      });
    }
    roRef.current.observe(ref.current);

    // Initial tick in case there's no resize
    pendingRef.current = window.requestAnimationFrame(doScroll);

    return () => {
      if (ref.current) roRef.current?.unobserve(ref.current);
      if (pendingRef.current) {
        cancelAnimationFrame(pendingRef.current);
        pendingRef.current = null;
      }
    };
  }, [isExpanded, doScroll, watchTransition, ref]);
}

type Props = {
  isLast: boolean;
  site: Site; // site includes site.realTimeLocation + site.amenities now
  /** Optional: pixels to keep below a sticky header */
  topOffset?: number; // e.g. 64
  /** Optional: id of a scroll container; defaults to window */
  scrollContainerId?: string;
};

const ListCard = forwardRef<HTMLDivElement, Props>(({ isLast, site, topOffset = 0, scrollContainerId }: Props, ref) => {
  const { t, i18n } = useTranslation();
  const { borderRadius } = useTheme();
  const { selectedSite, setSelectedSite } = useContext(SiteLookupMapContext);
  const { dataView, hasHistoricalAccess, hasRealTimeAccess } = useContext(SiteLookupContext);

  const isSelected = selectedSite?.id === site.id;
  const isExpandable = !!site.realTimeLocation || !!site.historicalView;
  const isExpanded = isSelected; // for now, we expand only the selected site
  const showHistoricalView = hasHistoricalAccess && dataView === 'historical' && !!site.historicalView;
  const showRealTimeView = hasRealTimeAccess && dataView === 'realtime' && !!site.realTimeLocation;

  const networkName = useMemo(() => {
    if ((site.networkCode === NetworkCode.NonNetworked || site.networkCode === NetworkCode.Unknown) && !!site.networkName) {
      return site.networkName;
    }
    return t(`enum.networkCode.${site.networkCode}`);
  }, [site, t]);

  const handleClick = () => {
    if (isSelected) {
      // already selected → unselect → collapse
      setSelectedSite(null);
    } else {
      // select new site
      setSelectedSite({ id: site.id, origin: 'list', overviewSelected: false });
    }
  };

  // Local ref + expose it via forwardRef
  const rootRef = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(ref, () => rootRef.current as HTMLDivElement, []);

  // Smoothly scroll the card to the top when it expands
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Merge our internal ref with the parent ref
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      // 1) keep our local ref up to date
      cardRef.current = node;

      // 2) propagate to parent
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref],
  );

  // Merge refs so parents can still hold a ref to the element
  useEffect(() => {
    if (!ref) return;
    if (typeof ref === 'function') ref(cardRef.current);
    else (ref as MutableRefObject<HTMLDivElement | null>).current = cardRef.current;
  }, [ref]);

  useScrollIntoViewOnExpand(cardRef, {
    isExpanded,
    topOffset,
    scrollContainerId,
  });

  return (
    <Stack
      ref={rootRef}
      paddingBottom={isLast ? 1.5 : 0}
      paddingX={1.5}
      gap={0.75}
      sx={{
        backgroundColor: isSelected ? '#f6f6fd' : 'inherit',
        borderRadius: borderRadius.lg,
        cursor: 'pointer',
        marginTop: isSelected ? '-9px' : '',
        paddingTop: isSelected ? '17px' : 1,
      }}
      onClick={handleClick}
    >
      <Stack direction="row" gap={1} ref={setRefs}>
        <Tooltip title={networkName} placement="top" arrow>
          <Box>
            <NetworkIcon networkCode={site.networkCode} iconUrl={site.networkIconUrl} sx={{ borderRadius: 0.5, height: 44, width: 44 }} />
          </Box>
        </Tooltip>

        <Stack gap={0.5} width="100%">
          <Stack direction="row" alignItems="center">
            <Box
              sx={{
                width: '50%',
                display: 'flex',
                alignItems: 'center', // vertically center icon + text
                minWidth: 0, // important so text ellipsis can shrink
              }}
            >
              {isExpandable && (isExpanded ? <ExpandMore fontSize="small" sx={{ mr: 0.5 }} /> : <ChevronRight fontSize="small" sx={{ mr: 0.5 }} />)}
              <Tooltip title={site.name} placement="top" arrow>
                <Typography
                  sx={{
                    marginRight: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: { xs: '9ch', lg: '14ch', xl: '18ch' },
                    width: 'fit-content',
                    whiteSpace: 'nowrap',
                    textTransform: 'capitalize',
                  }}
                  variant="body1"
                  fontWeight={500}
                >
                  {site.name.toLowerCase()}
                </Typography>
              </Tooltip>
            </Box>

            <MapPinpoint />

            <Typography color="text.secondary" variant="subtitle2" marginLeft={0.5}>
              {t('siteLookup.list.distance', { count: +site.distance.toFixed(2) })}
            </Typography>

            <Box sx={{ height: 20, marginLeft: 'auto' }}>
              <MapPin height={20} includedInReport={site.includedInReport} state={site.trackingState} status={site.status} width={20} />
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Chip
              size="small"
              sx={{ backgroundColor: grey[300], borderRadius: 0.5, fontWeight: 'medium', fontSize: rem(13), height: 20, '&>span': { paddingX: '4px' } }}
              label={t('siteLookup.list.ports', { count: site.portsCount })}
            />
            <Chip
              size="small"
              sx={{
                backgroundColor: alpha('#076FDB', 0.13),
                borderRadius: 0.5,
                fontWeight: 'medium',
                color: 'info.dark',
                fontSize: rem(13),
                height: 20,
                '&>span': { paddingX: '4px' },
              }}
              label={t('siteLookup.list.speed', { speed: site.maxSpeed })}
            />

            {isExpanded && showRealTimeView && (
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" marginLeft="auto">
                <RealTimeLocationChips siteId={site.id} rtl={site.realTimeLocation} />
              </Stack>
            )}

            {isExpanded && showHistoricalView && (
              <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap">
                {site.openDate && (
                  <Chip
                    size="small"
                    sx={{ backgroundColor: grey[300], borderRadius: 0.5, fontWeight: 'medium', fontSize: rem(13), height: 20, '&>span': { paddingX: '4px' } }}
                    label={t('siteLookup.list.opened', { date: formatUtcDateOnly(site.openDate, i18n.language) })}
                  />
                )}
                <HistoricalViewChips hv={site.historicalView} />
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>

      {isExpanded && showRealTimeView && (
        <Box marginBottom={'0px'} display={'flex'} flexDirection={'column'} alignItems={'center'} width={'100%'} height={150} mt={0.5}>
          <RealTimeLocationUtilization
            siteId={site.id}
            rtl={site.realTimeLocation}
            title={<strong style={{ paddingLeft: '10%', fontSize: '15px' }}>{t('siteLookup.list.utilization')}</strong>}
          />
        </Box>
      )}

      {isExpanded && showRealTimeView && <RealTimeLocationStationsTable siteId={site.id} rtl={site.realTimeLocation} />}

      {isExpanded && showRealTimeView && Array.isArray(site.amenities) && site.amenities.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={{ mt: 0.5, border: '1px solid', borderColor: 'divider' }}>
          <Table size="small" aria-label={t('siteLookup.list.amenities')}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('siteLookup.list.name')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('siteLookup.list.timeToWalk')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('siteLookup.list.hours')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {site.amenities.slice(0, 5).map((a: Amenity, i: number) => (
                <TableRow key={`${a.name}-${i}`}>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>{toMinutes(a.distanceWalkingMeters)} min</TableCell>
                  <TableCell>{formatHours(a.openingHours, site.realTimeLocation?.timezone)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isExpanded && showHistoricalView && (
        <HistoricalUtilizationHistogram siteId={site.id} buckets={ToHistogramBuckets(site.historicalView?.utilizationSeries?.buckets)} />
      )}

      {!isLast && <Divider sx={{ borderColor: isSelected ? 'transparent' : '', marginTop: 1 }} />}
    </Stack>
  );
});

export default ListCard;
